import type { CommandSpec } from '../../lib/shortcuts/index.js';
import { useLayout } from '../../state/layout.js';
import { frames, useProfiler } from './profiler-store.js';

/** Show a tool panel, or hide it when it is already on screen (like Undo History). */
function togglePanel(panel: string): void {
  const layout = useLayout.getState();
  const el = document.querySelector(`[data-dock-panel="${panel}"]`) as HTMLElement | null;
  if (el?.isConnected && el.offsetParent !== null) layout.toggle(panel);
  else layout.show(panel);
}

function step(delta: number): void {
  const s = useProfiler.getState();
  const list = frames.frames();
  if (!list.length) return;
  const at = s.selected === null ? list.length - 1 : frames.indexOf(s.selected);
  const next = list[Math.min(list.length - 1, Math.max(0, (at < 0 ? list.length - 1 : at) + delta))];
  if (next) s.select(next.seq);
}

/** Window / Profiler commands (registered by the shell, so they work while the panels are closed). */
export const PROFILER_COMMANDS: readonly CommandSpec[] = [
  {
    id: 'view.profiler',
    label: 'Profiler',
    category: 'Window',
    // No default key: Ctrl+Alt+P is Play's Step Frame (as in Unity).
    icon: 'gauge',
    keywords: 'performance cpu gpu frame time timeline fps',
    run: () => togglePanel('profiler'),
  },
  {
    id: 'view.frame-debugger',
    label: 'Frame Debugger',
    category: 'Window',
    icon: 'frames',
    keywords: 'render passes draw calls render target gpu',
    run: () => togglePanel('frameDebugger'),
  },
  {
    id: 'profiler.record',
    label: 'Profiler: Record / Pause',
    category: 'Profiler',
    icon: 'target',
    keywords: 'capture start stop',
    run: () => {
      const s = useProfiler.getState();
      s.record(!s.recording);
    },
  },
  { id: 'profiler.clear', label: 'Profiler: Clear', category: 'Profiler', icon: 'trash', run: () => useProfiler.getState().clear() },
  { id: 'profiler.prev-frame', label: 'Profiler: Previous Frame', category: 'Profiler', icon: 'chevronDown', repeat: true, run: () => step(-1) },
  { id: 'profiler.next-frame', label: 'Profiler: Next Frame', category: 'Profiler', icon: 'chevronUp', repeat: true, run: () => step(1) },
  {
    id: 'profiler.latest',
    label: 'Profiler: Follow Latest Frame',
    category: 'Profiler',
    icon: 'chevronRight',
    run: () => useProfiler.getState().setFollow(true),
  },
];
